import React, { useState, useEffect, useRef, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { validatePhoneNumber } from '../utils/phoneValidation';
import './Modal.css';
import './UserForm.css';
const apiUrl = process.env.REACT_APP_API_URL;

const BASE_URL = `${apiUrl}/business`;

// Generate 15-minute intervals between opening and closing hours (fallback: 12 PM - 11 PM)
const generate15MinSlots = (opening = '12:00', closing = '23:00') => {
  const cleanOpening = String(opening || '12:00').slice(0, 5);
  const cleanClosing = String(closing || '23:00').slice(0, 5);
  const [openH, openM] = cleanOpening.split(':').map(Number);
  const [closeH, closeM] = cleanClosing.split(':').map(Number);

  const startMins = (isNaN(openH) ? 12 : openH) * 60 + (isNaN(openM) ? 0 : openM);
  let endMins = (isNaN(closeH) ? 23 : closeH) * 60 + (isNaN(closeM) ? 0 : closeM);

  if (endMins <= startMins) {
    endMins += 24 * 60;
  }

  const slots = [];
  for (let m = startMins; m <= endMins; m += 15) {
    const normM = m % (24 * 60);
    const h = Math.floor(normM / 60);
    const min = normM % 60;
    const id = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    const displayH = h % 12 || 12;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const title = `${displayH}:${String(min).padStart(2, '0')} ${ampm}`;
    slots.push({ id, title });
  }
  return slots;
};

const isSlotInPast = (slotId, dateStr) => {
  if (!dateStr) return false;
  const now = new Date();
  const todayStr = formatLocalDate(now);
  if (dateStr !== todayStr) return false;

  const [hours, minutes] = slotId.split(':').map(Number);
  const slotTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
  return slotTime <= now;
};

const isSlotAllowedByRules = (slotId, dateStr, rules) => {
  if (!rules || !dateStr) return true;
  const slotTimeStr = slotId.length === 5 ? `${slotId}:00` : slotId;

  // 1) Specific date rules
  if (Array.isArray(rules.date_rules)) {
    const matchDateRule = rules.date_rules.find((r) => r.date === dateStr);
    if (matchDateRule) {
      if (!matchDateRule.time_range) {
        return matchDateRule.allowed !== false;
      }
      const { start, end } = matchDateRule.time_range;
      if (start <= slotTimeStr && slotTimeStr < end) {
        return matchDateRule.allowed !== false;
      }
    }
  }

  // 2) Weekly rules (0 = Monday ... 6 = Sunday)
  if (Array.isArray(rules.weekly_rules)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const weekday = (d.getDay() + 6) % 7;

    for (const rule of rules.weekly_rules) {
      if (Array.isArray(rule.days_of_week) && rule.days_of_week.includes(weekday)) {
        if (!rule.time_range) {
          return rule.allowed !== false;
        }
        const { start, end } = rule.time_range;
        if (start <= slotTimeStr && slotTimeStr < end) {
          return rule.allowed !== false;
        }
      }
    }
  }

  return true;
};

const formatLocalDate = (dateObj) => {
  if (!dateObj) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const UserForm = ({
  onSubmit,
  allentered_check,
  initialData = true,
  queue_info,
  auto_queue,
  usercheck,
  isPhoneRequired,
  business_id,
  tables = [],
  business_type = '',
  isReservation = false,
  reservedQueue = null,
  businessMetadata = null,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    country_code: '91',
    no_of_people: business_type === 'other' ? 1 : '',
    queue_id: '',
    queue_type: usercheck ? '' : 'waiting',
    estimated_arrival_time: '',
    table_no: '',
    table_id: '',
    remarks: '',
    metadata: {},
  });
  const nameInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [selectedQueueType, setSelectedQueueType] = useState('waiting');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reservationDays, setReservationDays] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [slotError, setSlotError] = useState('');
  const [bizMeta, setBizMeta] = useState(businessMetadata || null);

  useEffect(() => {
    if (businessMetadata) {
      setBizMeta(businessMetadata);
      return;
    }
    const cached = localStorage.getItem('cachedBusinessInfo');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.business_metadata) {
          setBizMeta(parsed.business_metadata);
          return;
        }
      } catch (err) {
        console.warn('Error reading cached business info:', err);
      }
    }
    if (business_id) {
      fetch(`${BASE_URL}/business_info?business_id=${business_id}`)
        .then((res) => res.json())
        .then((res) => {
          if (res?.data?.business_metadata) {
            setBizMeta(res.data.business_metadata);
          }
        })
        .catch((err) => console.error('Error fetching business info metadata:', err));
    }
  }, [businessMetadata, business_id]);

  const reservationRules = bizMeta?.reservation_rules || null;

  const computedSlots = useMemo(() => {
    const opening = bizMeta?.opening_hours || '12:00';
    const closing = bizMeta?.closing_hours || '23:00';
    return generate15MinSlots(opening, closing);
  }, [bizMeta?.opening_hours, bizMeta?.closing_hours]);

  const minReservationDate = useMemo(() => {
    if (reservationDays?.min_date) return reservationDays.min_date;
    const d = new Date();
    if (reservationRules?.min_advance_booking_days) {
      d.setDate(d.getDate() + reservationRules.min_advance_booking_days);
    }
    return formatLocalDate(d);
  }, [reservationDays?.min_date, reservationRules?.min_advance_booking_days]);

  const maxReservationDate = useMemo(() => {
    if (reservationDays?.max_date) return reservationDays.max_date;
    if (reservationRules?.max_advance_booking_days) {
      const d = new Date();
      d.setDate(d.getDate() + reservationRules.max_advance_booking_days);
      return formatLocalDate(d);
    }
    return undefined;
  }, [reservationDays?.max_date, reservationRules?.max_advance_booking_days]);

  useEffect(() => {
    if (isReservation) {
      setSelectedQueueType('reserved');
      const reservedId = reservedQueue?.queue_id || queue_info?.find((q) => q.queue_type === 'reserved')?.queue_id || '';
      setFormData((prev) => ({
        ...prev,
        queue_type: 'reserved',
        queue_id: reservedId || prev.queue_id,
        estimated_arrival_time: prev.estimated_arrival_time || '',
      }));
    }
  }, [isReservation, reservedQueue, queue_info]);

  // Initialize default selectedDate when queue_type is reserved
  useEffect(() => {
    if (selectedQueueType === 'reserved') {
      if (formData.estimated_arrival_time) {
        const d = new Date(formData.estimated_arrival_time * 1000);
        setSelectedDate(formatLocalDate(d));
      } else if (!selectedDate) {
        setSelectedDate(minReservationDate || formatLocalDate(new Date()));
      }
    }
  }, [selectedQueueType, formData.estimated_arrival_time, selectedDate, minReservationDate]);

  // Fetch available reservation days when reserved (for unavailable_dates, min_date, max_date)
  useEffect(() => {
    if ((isReservation || selectedQueueType === 'reserved') && business_id) {
      const fetchReservationDays = async () => {
        try {
          const response = await fetch(`${BASE_URL}/reservation_days?business_id=${business_id}`);
          const result = await response.json();
          if (result && result.data) {
            setReservationDays(result.data);
            const initialMin = result.data.min_date || minReservationDate;
            if (initialMin) {
              setSelectedDate((prev) => {
                if (!prev) return initialMin;
                if (prev < initialMin) return initialMin;
                return prev;
              });
            }
          }
        } catch (error) {
          console.error('Error fetching reservation days:', error);
        }
      };

      fetchReservationDays();
    }
  }, [isReservation, selectedQueueType, business_id, minReservationDate]);

  // Sync selectedSlotId with formData.estimated_arrival_time
  useEffect(() => {
    if (formData.estimated_arrival_time && selectedDate) {
      const d = new Date(formData.estimated_arrival_time * 1000);
      const slotDateStr = formatLocalDate(d);
      if (slotDateStr === selectedDate) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setSelectedSlotId(`${hh}:${mm}`);
      } else {
        setSelectedSlotId('');
      }
    }
  }, [formData.estimated_arrival_time, selectedDate]);

  const handleSlotDropdownChange = (e) => {
    const slotId = e.target.value;
    setSelectedSlotId(slotId);
    setSlotError('');

    if (!slotId) {
      setFormData((prev) => ({
        ...prev,
        estimated_arrival_time: '',
      }));
      return;
    }

    const isPast = isSlotInPast(slotId, selectedDate);
    const isAllowed = isSlotAllowedByRules(slotId, selectedDate, reservationRules);
    const isAvailable = isAllowed && !isPast;

    // For customers, strictly block disabled slots
    if (usercheck && !isAvailable) {
      setSlotError('This time slot is unavailable. Please select another time.');
      return;
    }

    const [hours, minutes] = slotId.split(':');
    const [year, month, day] = (selectedDate || formatLocalDate(new Date())).split('-').map(Number);
    const targetDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    const timestamp = Math.floor(targetDate.getTime() / 1000);
    setFormData((prev) => ({
      ...prev,
      estimated_arrival_time: timestamp,
    }));
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSelectedSlotId('');
    setSlotError('');
    setFormData((prev) => ({
      ...prev,
      estimated_arrival_time: '',
    }));
  };

  // const fetchTables = async (token) => {
  //   try {
  //     const response = await fetch(`${BASE_URL}/get_business_tables`, {
  //       method: 'GET',
  //       credentials: 'include',
  //     });
  //     const data = await response.json();
  //     setTables(data.data || []);
  //   } catch (error) {
  //     console.error('Error fetching tables:', error);
  //   }
  // };
  // useEffect(() => {
    
  //   fetchTables();
  // }, []);
  
  
  useEffect(() => {
    if (usercheck) {
      const savedFormData = {
        name: localStorage.getItem('name') || '',
        phone: localStorage.getItem('phone') || '',
        country_code: (localStorage.getItem('country_code') || '91').replace(/^\+/, ''),
        no_of_people: business_type === "other" ? 1 : (localStorage.getItem('no_of_people') || '')
      };
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...savedFormData
      }));
    }
  }, [usercheck, business_type]);

  useEffect(() => {
    // Focus the Name input when the form loads
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);


  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };


  useEffect(() => {
    if (business_id === 10 || business_id === 11) {
      setFormData(prevFormData => ({
        ...prevFormData,
        no_of_people: 1
      }));
    }
  }, [business_id]);

  useEffect(() => {
    if (initialData) {
       // Check if the queue_type is 'reserved' and set the selected queue type
       if (initialData.queue_type === 'reserved') {
        setSelectedQueueType('reserved');
      }
      
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: initialData.customer_name || prevFormData.name || initialData.name,
        phone: initialData.phone_number || prevFormData.phone || initialData.phone,
        country_code: (initialData.country_code ? String(initialData.country_code).replace(/^\+/, '') : (prevFormData.country_code || '91')),
        no_of_people: initialData.no_of_people || prevFormData.no_of_people,
        queue_type: initialData.queue_type || prevFormData.queue_type,
        table_no: initialData.table_no || prevFormData.table_no,
        estimated_arrival_time: initialData.estimated_arrival_time
          ? (new Date(initialData.estimated_arrival_time).getTime() / 1000) + 19800
          : (prevFormData.estimated_arrival_time 
            ? prevFormData.estimated_arrival_time
            : ''),
        remarks: (initialData.notes?.[0]?.note_text) || prevFormData.remarks || '',
        table_id: initialData.table_id || prevFormData.table_id,
        metadata: {
          ...prevFormData.metadata,
          next_queue_id: initialData.next_queue_id || prevFormData.metadata?.next_queue_id || ''
        },
        ...(initialData.queue_type === 'reserved' && {
          queue_id: initialData.queue_id || prevFormData.queue_id,
        }),
      }));
      
    }
  }, [initialData]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'metadata.next_queue_id') {
      const reservedId = reservedQueue?.queue_id || queue_info?.find(q => q.queue_type === 'reserved')?.queue_id;
      setFormData(prev => ({
        ...prev,
        queue_id: isReservation ? (reservedId || prev.queue_id) : value, // Update queue_id for UI reflection
        metadata: {
          ...prev.metadata,
          next_queue_id: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (['name', 'phone', 'no_of_people', 'table_no'].includes(name)) {
      localStorage.setItem(name, value);
    }

    if (name === 'queue_type') {
      setSelectedQueueType(value); 
      if (value === 'reserved') {
        const foundReserved = reservedQueue || queue_info?.find(queue => queue.queue_type === 'reserved');
        if (foundReserved) {
          setFormData(prevFormData => ({
            ...prevFormData,
            queue_id: foundReserved.queue_id
          }));
        }
      } else if (value === 'waiting') {
        setFormData(prevFormData => ({
          ...prevFormData,
          queue_id: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (isPhoneRequired || (formData.phone && formData.phone.trim() !== '')) {
      const validation = validatePhoneNumber(formData.phone, formData.country_code, isPhoneRequired);
      if (!validation.isValid) {
        setPhoneError(validation.message);
        return;
      }
    }
    if (selectedQueueType === 'reserved') {
      if (!formData.estimated_arrival_time || !selectedSlotId) {
        setSlotError('Please select a reservation time slot.');
        return;
      }
      if (usercheck) {
        const isPast = isSlotInPast(selectedSlotId, selectedDate);
        const isAllowed = isSlotAllowedByRules(selectedSlotId, selectedDate, reservationRules);
        if (isPast || !isAllowed) {
          setSlotError('The selected time slot is unavailable.');
          return;
        }
      }
    }
    setSlotError('');
    setIsSubmitting(true);
  
    let dataToSubmit = {
      ...formData,
      country_code: (formData.country_code || '91').replace(/^\+/, ''),
      phone: formData.phone ? String(formData.phone).trim() : '',
    };
  
    // Remove unnecessary fields based on queue type
    if (usercheck) {
      delete dataToSubmit.table_no;
      delete dataToSubmit.table_id;
    }
  
    if (selectedQueueType === 'waiting') {
      delete dataToSubmit.estimated_arrival_time;
      delete dataToSubmit.remarks;
    }
  
    // Remove queue_id if it is null or an empty string
    if (!dataToSubmit.queue_id) {
      delete dataToSubmit.queue_id;
    }
    if (!dataToSubmit.table_id) {
      delete dataToSubmit.table_id;
    }
  
    try {
      if (allentered_check) {
        onSubmit(dataToSubmit); // Do not await
      } else {
        await onSubmit(dataToSubmit); // Await if allentered_check is false
      }
    } finally {
      setIsSubmitting(false); // Always reset isSubmitting regardless of the condition
    }    
  };
  

  return (
    <div>
    <form onSubmit={handleSubmit} className="user-form">
      <div className="form-group">
        <label>Name:</label>
        <input
          type="text"
          name="name"
          ref={nameInputRef} // Attach the ref to the Name input
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group phone-form-group">
        <label>Phone:</label>
        <PhoneInput
          country="in"
          preferredCountries={['in', 'us', 'gb', 'ae']}
          enableSearch={true}
          searchPlaceholder="Search country..."
          searchNotFound="No country found"
          value={formData.phone ? `${formData.country_code || '91'}${formData.phone}` : (formData.country_code ? `${formData.country_code}` : '')}
          onChange={(value, countryData) => {
            const dialCode = countryData?.dialCode ? String(countryData.dialCode) : '91';
            const nationalNumber = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
            setFormData(prev => ({
              ...prev,
              country_code: dialCode,
              phone: nationalNumber,
            }));
            if (usercheck) {
              localStorage.setItem('country_code', dialCode);
              localStorage.setItem('phone', nationalNumber);
            }
            if (phoneError) {
              setPhoneError('');
            }
          }}
          inputProps={{
            name: 'phone',
            required: isPhoneRequired,
            autoComplete: 'tel',
          }}
          specialLabel=""
        />
        {phoneError && <span className="phone-error-text">{phoneError}</span>}
      </div>

      {/* Only show No of People if business_type is not "Others" and business_id is not 10, 11, or 59 */}
      {business_type !== "other" && (business_id !== 10 && business_id !== 11 && business_id !== 59) && (
        <div className="form-group">
          <label>No of People:</label>
          <input
            type="number"
            name="no_of_people"
            value={formData.no_of_people}
            onChange={handleChange}
            required
          />
        </div>
      )}

      {/* Only show Table fields if business_type is not "Others" and not usercheck */}
      {business_type !== "other" && !usercheck && (
        <>
          {(tables ?? []).length > 0 ? (
            <div className="form-group">
              <label>Table:</label>
              <select
                name="table_id"
                value={formData.table_id}
                onChange={(e) => handleChange({
                  target: {
                    name: 'table_id',
                    value: e.target.value ? parseInt(e.target.value, 10) : null
                  }
                })}
              >
                <option value="">Select Table (Optional)</option>
                {tables.map((table) => (
                  <option key={table.id} value={table.id}>
                    {table.name} (Status: {table.status})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label>Table No:</label>
              <input
                type="text"
                name="table_no"
                value={formData.table_no}
                onChange={handleChange}
                placeholder="Enter Table No (Optional)"
              />
            </div>
          )}
        </>
      )}

      {/* Queue Type dropdown */}
      {!usercheck && (
        <div className="form-group">
          <label>Entry Type:</label>
          <select name="queue_type" value={formData.queue_type} onChange={handleChange} required>
            <option value="">Select Entry Type</option>
            <option value="waiting">Walk-in</option>
            <option value="reserved">Reservation</option>
          </select>
        </div>
      )}

      {((usercheck && !auto_queue) || (!usercheck && (selectedQueueType === 'waiting' || selectedQueueType === 'reserved') && !auto_queue)) && (
        <div className="form-group">
          <label>Category:</label>
          <select 
            name={selectedQueueType === 'reserved' ? 'metadata.next_queue_id' : 'queue_id'} 
            value={formData.queue_id} 
            onChange={handleChange} 
            required
          >
            <option value="">Select Category</option>
            {queue_info
              .filter((queue) => queue.queue_type !== 'reserved')
              .map((queue) => (
                <option key={queue.queue_id} value={queue.queue_id}>
                  {queue.queue_name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* Conditionally render fields if the queue type is 'reserved' */}
      {selectedQueueType === 'reserved' && (
        <>
          <div className="form-group">
            <label>Reservation Date:</label>
            <input
              type="date"
              name="estimated_arrival_date"
              min={minReservationDate || formatLocalDate(new Date())}
              max={maxReservationDate || undefined}
              value={selectedDate}
              onChange={handleDateChange}
              required
            />
            {reservationDays?.unavailable_dates?.includes(selectedDate) && (
              <span className="reservation-notice-text">
                Note: Limited availability on this date. Reservation subject to confirmation.
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Select Arrival Time Slot:</label>
            <select
              name="estimated_arrival_slot"
              className="slot-select"
              value={selectedSlotId}
              onChange={handleSlotDropdownChange}
              required
            >
              <option value="">Select Arrival Time</option>
              {computedSlots.map((slot) => {
                const isPast = isSlotInPast(slot.id, selectedDate);
                const isAllowed = isSlotAllowedByRules(slot.id, selectedDate, reservationRules);
                const isAvailable = isAllowed && !isPast;

                let label = slot.title;
                if (!isAvailable) {
                  if (isPast) {
                    label += ' (Past)';
                  } else {
                    label += usercheck ? ' (Unavailable)' : ' (Disabled)';
                  }
                }

                return (
                  <option
                    key={slot.id}
                    value={slot.id}
                    disabled={usercheck ? !isAvailable : false}
                  >
                    {label}
                  </option>
                );
              })}
            </select>
            {slotError && <span className="phone-error-text">{slotError}</span>}
          </div>

          <div className="form-group">
            <label>Remarks:</label>
            <input
              type="text"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
            />
          </div>
        </>
      )}

    {usercheck && (
      <div className="terms">
        <label className="terms-label">
          <span style={{ paddingBottom: '4px', color: 'darkslategray' }}>
         By clicking the button below, you agree to our {' '}
            <span className="terms-link" onClick={handleModalToggle}>
              terms and conditions
            </span>
          </span>
        </label>
      </div>
    )}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </form>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleModalToggle}>
          <div className="modal-content" style={{ maxWidth: '290px' }} onClick={(e) => e.stopPropagation()}>
            <h2>Terms and Conditions</h2>
            <p class="terms-content">
  By submitting your information in this form, you agree to the following terms:
  <br /><br />
  <strong>Use of Contact Information:</strong><br />
  Your contact details, including your phone number, may be used by us to send transactional updates via WhatsApp, such as order confirmations, status updates, or other related information.
  <br /><br />
  <strong>Customer Assistance:</strong><br />
  The restaurant may contact you in case you leave behind personal belongings or for other customer service purposes.
  <br /><br />
  <strong>Data Privacy:</strong><br />
  Your personal data will be stored securely and will not be shared with third parties without your consent, except as necessary to provide the requested services.
  <br /><br />
  <strong>Opt-Out Option:</strong><br />
  You may opt out of marketing or promotional communications at any time by contacting the restaurant directly.
  <br /><br />
  <strong>Acceptance of Terms:</strong><br />
  By submitting your information, you acknowledge that you have read, understood, and agreed to these terms and conditions.
</p>

            <button onClick={handleModalToggle}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserForm;
